import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Heart } from 'lucide-react';
import { toast } from 'sonner';

import { getSource } from '@/sources/registry';
import type { ComicDetails, ComicSource, SourceAuth, Tag } from '@/sources/types';
import { Button } from '@/components/ui/button';
import { badgeVariants } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { useSourceAuth } from '@/features/auth';
import {
  addSourceFavorite,
  canSaveInApp,
  needsSourceFavoriteAuth,
  removeSourceFavorite,
  saveFavorite,
  supportsSourceFavorites,
  unsaveFavorite,
} from '@/features/library';
import { BlacklistWarning } from '@/features/blacklist';
import { SourceErrorBanner } from '@/features/reader';
import { GalleryPreviews } from '@/features/gallery-previews';
import { SourceComments } from '@/features/comments';
import { cn } from '@/lib/utils';
import { JMCOMIC_SOURCE_ID } from '@/sources/jmcomic/source';
import {
  addFavorite as addLocalFavorite,
  getFavorite,
  isAppSaved,
  isSourceSaved,
  markSourceFavorite as addLocalSourceFavorite,
  removeFavorite as removeLocalFavorite,
  unmarkSourceFavorite as removeLocalSourceFavorite,
} from '@/db/favorites';
import { getLatestComicHistory } from '@/db/history';
import type { HistoryRow } from '@/db/schema';
import type { ReadGroup, ReadUnit } from '@/sources/types';
import { isLocationStateWithBack, safeInternalPath } from '@/lib/navigationState';
import {
  copyDebugReport,
  getManualDebugReportText,
  recordDiagnostic,
  summarizeDiagnosticError,
} from '@/features/diagnostics';
import { DebugReportFallbackDialog } from '@/features/diagnostics/DebugReportFallbackDialog';

function tagSearchSpec(tag: Tag): string {
  if (tag.id.includes(':')) return tag.id;
  return tag.namespace ? `${tag.namespace}:${tag.name}` : tag.name;
}

function tagSearchUrl(sourceId: string, tag: Tag): string {
  const params = new URLSearchParams();
  if (sourceId === JMCOMIC_SOURCE_ID) {
    params.set('q', tag.name);
    return `/source/${sourceId}?${params.toString()}`;
  }
  params.set('inc', tagSearchSpec(tag));
  return `/source/${sourceId}?${params.toString()}`;
}

function uploaderSearchUrl(sourceId: string, uploader: string): string {
  const params = new URLSearchParams();
  if (sourceId === JMCOMIC_SOURCE_ID) {
    params.set('q', uploader);
    return `/source/${sourceId}?${params.toString()}`;
  }
  params.set('uploader', uploader);
  return `/source/${sourceId}?${params.toString()}`;
}

function formatRating(rating: number): string {
  return Number.isInteger(rating) ? rating.toFixed(0) : rating.toFixed(2);
}

function UploaderLine({ comic, source }: { comic: ComicDetails; source: ComicSource }) {
  const uploader = comic.uploader?.trim();
  if (!uploader) return null;

  return (
    <>
      {' · uploaded by '}
      {source.capabilities.uploaderSearch || source.id === JMCOMIC_SOURCE_ID ? (
        <Link to={uploaderSearchUrl(source.id, uploader)} className="underline underline-offset-4">
          {uploader}
        </Link>
      ) : (
        uploader
      )}
    </>
  );
}

function AppSaveAction({
  source,
  ready,
  saving,
  appSaved,
  onToggle,
  compact = false,
}: {
  source: ComicSource;
  ready: boolean;
  saving: boolean;
  appSaved: boolean;
  onToggle: () => void;
  compact?: boolean;
}) {
  if (!canSaveInApp(source)) return null;

  const disabled = saving || !ready;
  const label = saving ? 'Saving to Bunko...' : appSaved ? 'Saved in Bunko' : 'Save to Bunko';

  return (
    <Button
      type="button"
      variant={appSaved ? 'secondary' : 'outline'}
      size={compact ? 'icon' : 'default'}
      aria-pressed={appSaved}
      aria-label={compact ? label : undefined}
      disabled={disabled}
      onClick={onToggle}
    >
      <Heart className={cn('h-4 w-4', appSaved && 'fill-current')} />
      {!compact && <span>{label}</span>}
    </Button>
  );
}

function SourceFavoriteAction({
  source,
  auth,
  authReady,
  ready,
  saving,
  sourceSaved,
  onToggle,
  compact = false,
}: {
  source: ComicSource;
  auth: SourceAuth | undefined;
  authReady: boolean;
  ready: boolean;
  saving: boolean;
  sourceSaved: boolean;
  onToggle: () => void;
  compact?: boolean;
}) {
  if (!supportsSourceFavorites(source)) return null;

  const requiresAuth = needsSourceFavoriteAuth(source);
  if (requiresAuth && authReady && (!auth || auth.anonymous)) {
    return (
      <Button asChild variant="outline" size={compact ? 'icon' : 'default'}>
        <Link
          to={`/connect/${source.id}`}
          aria-label={compact ? `Connect ${source.name} to use source favorites` : undefined}
        >
          <Heart className="h-4 w-4" />
          {!compact && <span>Connect to save on {source.name}</span>}
        </Link>
      </Button>
    );
  }

  const disabled = saving || !ready || (requiresAuth && !authReady);
  const label = saving
    ? 'Updating source...'
    : sourceSaved
      ? `Favorited on ${source.name}`
      : `Add to ${source.name} favorites`;

  return (
    <Button
      type="button"
      variant={sourceSaved ? 'secondary' : 'outline'}
      size={compact ? 'icon' : 'default'}
      aria-pressed={sourceSaved}
      aria-label={compact ? label : undefined}
      disabled={disabled}
      onClick={onToggle}
    >
      <Heart className={cn('h-4 w-4', sourceSaved && 'fill-current')} />
      {!compact && <span>{label}</span>}
    </Button>
  );
}

/**
 * Gallery detail page. Source-agnostic — looks up the source from the
 * registry and renders whatever the source's `getComic()` returns.
 */
export function ComicPage() {
  const { sourceId, comicId } = useParams<{ sourceId: string; comicId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const back = isLocationStateWithBack(location.state) ? location.state.back : undefined;
  const source = sourceId ? getSource(sourceId) : undefined;
  const id = comicId ? decodeURIComponent(comicId) : undefined;
  const { auth, ready: authReady } = useSourceAuth(source);

  const [comic, setComic] = useState<ComicDetails | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<HistoryRow | null>(null);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [favoriteReady, setFavoriteReady] = useState(false);
  const [appSaved, setAppSaved] = useState(false);
  const [sourceSaved, setSourceSaved] = useState(false);
  const [favoriteSaving, setFavoriteSaving] = useState(false);
  const [sourceFavoriteSaving, setSourceFavoriteSaving] = useState(false);
  const [favoriteError, setFavoriteError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);
  const [manualDebugReportText, setManualDebugReportText] = useState<string | null>(null);

  useEffect(() => {
    if (!source || !id) return;
    if (!authReady) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    recordDiagnostic('source', 'comic-detail:start', { sourceId: source.id, comicId: id });
    source
      .getComic(id, auth)
      .then((c) => {
        if (!cancelled) {
          setComic(c);
          recordDiagnostic('source', 'comic-detail:success', { sourceId: source.id, comicId: id });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err);
          recordDiagnostic(
            'source',
            'comic-detail:error',
            { sourceId: source.id, comicId: id, error: summarizeDiagnosticError(err) },
            'error',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source, id, authReady, auth, loadNonce]);

  useEffect(() => {
    if (!source || !id) return;
    let cancelled = false;
    getLatestComicHistory(source.id, id)
      .then((row) => {
        if (!cancelled) setHistory(row ?? null);
      })
      .catch(() => {
        if (!cancelled) setHistory(null);
      });
    return () => {
      cancelled = true;
    };
  }, [source, id]);

  useEffect(() => {
    if (!source || !id) {
      setFavoriteReady(true);
      setAppSaved(false);
      setSourceSaved(false);
      return;
    }
    let cancelled = false;
    setFavoriteReady(false);
    setFavoriteError(null);
    getFavorite(source.id, id)
      .then((row) => {
        if (!cancelled) {
          setAppSaved(row ? isAppSaved(row) : false);
          setSourceSaved(row ? isSourceSaved(row) : false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAppSaved(false);
          setSourceSaved(false);
        }
      })
      .finally(() => {
        if (!cancelled) setFavoriteReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source, id]);

  if (!source || !id) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">Not found</h1>
        <Link to="/" className="text-sm underline">
          ← Back to sources
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid gap-6 md:grid-cols-[240px_1fr]">
        <Skeleton className="aspect-[3/4] w-full" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (error || !comic) {
    return (
      <>
        <SourceErrorBanner
          error={error ?? new Error('unknown error')}
          sourceId={source.id}
          onRetry={() => setLoadNonce((current) => current + 1)}
          onCopyDebugReport={() => {
            void copyDebugReport({ sourceId: source.id, comicId: id })
              .then(() => toast.success('Debug report copied.'))
              .catch((err: unknown) => {
                const reportText = getManualDebugReportText(err);
                if (reportText) {
                  setManualDebugReportText(reportText);
                  return;
                }
                const message = err instanceof Error ? err.message : String(err);
                toast.error(`Could not copy debug report: ${message}`);
              });
          }}
        />
        <DebugReportFallbackDialog
          reportText={manualDebugReportText}
          onOpenChange={(open) => {
            if (!open) setManualDebugReportText(null);
          }}
        />
      </>
    );
  }

  const readPath = `/source/${source.id}/comic/${encodeURIComponent(id)}/read/1`;
  const continuePath = `/source/${source.id}/comic/${encodeURIComponent(id)}/read/${history?.lastPage ?? 1}`;
  const readUnits =
    comic.readGroups?.flatMap((group) => group.units.map((unit) => ({ group, unit }))) ?? [];
  const readUnitCount = readUnits.length;
  const hasMultipleReadUnits = readUnits.length > 1;
  const shouldShowReadGroups =
    hasMultipleReadUnits || (readUnitCount === 1 && comic.pageCount <= 0);
  const defaultReadTarget = readUnits[0];
  const primaryCountLabel =
    readUnitCount > 0 && comic.pageCount <= 0
      ? `${readUnitCount} ${readUnitCount === 1 ? 'chapter' : 'chapters'}`
      : `${comic.pageCount} pages`;
  const readPathForDefault = defaultReadTarget
    ? readPathForUnit(source.id, id, defaultReadTarget.unit.id, 1)
    : readPath;
  const continuePathForHistory = history?.readUnitId
    ? readPathForUnit(source.id, id, history.readUnitId, history.lastPage)
    : continuePath;
  const progressTotalPages = history?.totalPages ?? comic.pageCount;
  const hasProgress = Boolean(
    history &&
    history.lastPage > 1 &&
    progressTotalPages > 0 &&
    history.lastPage <= progressTotalPages,
  );
  const tagFoldLimit = 4;
  const visibleTags = tagsExpanded ? comic.tags : comic.tags.slice(0, tagFoldLimit);
  const hiddenTagCount = Math.max(0, comic.tags.length - visibleTags.length);

  async function handleFavoriteToggle() {
    if (!source || !comic || favoriteSaving) return;
    setFavoriteSaving(true);
    setFavoriteError(null);
    const nextFavoriteState = !appSaved;
    try {
      if (nextFavoriteState) {
        await saveFavorite(source, comic, {
          saveApp: addLocalFavorite,
          unsaveApp: removeLocalFavorite,
          markSource: addLocalSourceFavorite,
          unmarkSource: removeLocalSourceFavorite,
        });
        setAppSaved(true);
      } else {
        await unsaveFavorite(source, comic.id, {
          saveApp: addLocalFavorite,
          unsaveApp: removeLocalFavorite,
          markSource: addLocalSourceFavorite,
          unmarkSource: removeLocalSourceFavorite,
        });
        setAppSaved(false);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setFavoriteError(`Failed to update Bunko save: ${message}`);
    } finally {
      setFavoriteSaving(false);
    }
  }

  async function handleSourceFavoriteToggle() {
    if (!source || !comic || sourceFavoriteSaving) return;
    setSourceFavoriteSaving(true);
    setFavoriteError(null);
    const nextFavoriteState = !sourceSaved;
    try {
      if (nextFavoriteState) {
        await addSourceFavorite(source, comic, auth, {
          saveApp: addLocalFavorite,
          unsaveApp: removeLocalFavorite,
          markSource: addLocalSourceFavorite,
          unmarkSource: removeLocalSourceFavorite,
        });
      } else {
        await removeSourceFavorite(source, comic.id, auth, {
          saveApp: addLocalFavorite,
          unsaveApp: removeLocalFavorite,
          markSource: addLocalSourceFavorite,
          unmarkSource: removeLocalSourceFavorite,
        });
      }
      setSourceSaved(nextFavoriteState);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setFavoriteError(`Failed to update ${source.name} source favorites: ${message}`);
    } finally {
      setSourceFavoriteSaving(false);
    }
  }

  return (
    <BlacklistWarning comic={comic} alwaysShowChildren>
      <div className="flex flex-col gap-6 pb-20 md:pb-0">
        <button
          type="button"
          onClick={() => {
            navigate(safeInternalPath(back?.fromPath ?? '/'), {
              replace: Boolean(back),
              state: back?.restoreComicKey ? { restoreComicKey: back.restoreComicKey } : undefined,
            });
          }}
          className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {back ? back.fromLabel : 'Sources'}
        </button>
        <header className="grid gap-6 md:grid-cols-[240px_1fr]">
          <img
            src={comic.coverUrl}
            alt=""
            referrerPolicy="no-referrer"
            className="aspect-[3/4] w-full rounded object-cover"
          />
          <div className="flex flex-col gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{comic.title}</h1>
            {comic.altTitle && <p className="text-sm text-muted-foreground">{comic.altTitle}</p>}
            <p className="text-sm">
              {primaryCountLabel}
              {comic.rating !== undefined ? ` · Rating ${formatRating(comic.rating)}` : ''}
              {comic.language ? ` · ${comic.language}` : ''}
              <UploaderLine comic={comic} source={source} />
            </p>
            <div className="flex flex-wrap gap-1.5">
              {visibleTags.map((tag) => (
                <Link
                  key={`${tag.namespace ?? ''}:${tag.id}`}
                  to={tagSearchUrl(source.id, tag)}
                  className={cn(badgeVariants({ variant: 'secondary' }), 'hover:no-underline')}
                >
                  {tag.namespace ? `${tag.namespace}:${tag.name}` : tag.name}
                </Link>
              ))}
              {hiddenTagCount > 0 && (
                <button
                  type="button"
                  onClick={() => setTagsExpanded(true)}
                  className="rounded-full border border-border/60 px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-accent/30 hover:text-foreground"
                >
                  Show {hiddenTagCount} more
                </button>
              )}
              {tagsExpanded && comic.tags.length > tagFoldLimit && (
                <button
                  type="button"
                  onClick={() => setTagsExpanded(false)}
                  className="rounded-full border border-border/60 px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-accent/30 hover:text-foreground"
                >
                  Show less
                </button>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button asChild>
                <Link
                  to={hasProgress ? continuePathForHistory : readPathForDefault}
                  state={location.state}
                >
                  {hasProgress ? 'Continue' : 'Read'}
                </Link>
              </Button>
              {hasProgress && (
                <Button asChild variant="outline">
                  <Link to={readPathForDefault} state={location.state}>
                    Start over
                  </Link>
                </Button>
              )}
              {comic.sourceUrl && (
                <Button asChild variant="outline">
                  <a href={comic.sourceUrl} target="_blank" rel="noreferrer">
                    Open source
                  </a>
                </Button>
              )}
              <AppSaveAction
                source={source}
                ready={favoriteReady}
                saving={favoriteSaving}
                appSaved={appSaved}
                onToggle={handleFavoriteToggle}
              />
              <SourceFavoriteAction
                source={source}
                auth={auth}
                authReady={authReady}
                ready={favoriteReady}
                saving={sourceFavoriteSaving}
                sourceSaved={sourceSaved}
                onToggle={handleSourceFavoriteToggle}
              />
            </div>
            {favoriteError && (
              <p className="text-sm text-destructive" role="alert">
                {favoriteError}
              </p>
            )}
          </div>
        </header>

        {comic.pagePreviews && (
          <>
            <Separator />
            <GalleryPreviews
              source={source}
              comicId={id}
              initialBatch={comic.pagePreviews}
              auth={auth}
              locationState={location.state}
            />
          </>
        )}

        {comic.description && (
          <>
            <Separator />
            <p className="whitespace-pre-wrap text-sm">{comic.description}</p>
          </>
        )}
        {shouldShowReadGroups && (
          <>
            <Separator />
            <ReadGroups
              sourceId={source.id}
              comicId={id}
              groups={comic.readGroups ?? []}
              currentHistory={history}
              locationState={location.state}
            />
          </>
        )}
        {source.capabilities.comments && source.getComments && (
          <>
            <Separator />
            <SourceComments source={source} comicId={id} auth={auth} />
          </>
        )}
      </div>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 p-3 pb-[calc(0.75rem_+_var(--app-safe-area-bottom))] backdrop-blur md:hidden">
        <div className="container flex items-center justify-between gap-3 p-0">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-muted-foreground">
              {hasProgress
                ? `Continue from page ${history!.lastPage}`
                : readUnitCount > 0 && comic.pageCount <= 0
                  ? `${readUnitCount} ${readUnitCount === 1 ? 'chapter' : 'chapters'}`
                  : `${comic.pageCount}p total`}
            </p>
            {hasProgress && (
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary"
                  style={{
                    width: `${Math.round((history!.lastPage / progressTotalPages) * 100)}%`,
                  }}
                />
              </div>
            )}
          </div>
          {hasProgress && (
            <Button asChild variant="outline">
              <Link to={readPathForDefault} state={location.state}>
                Start
              </Link>
            </Button>
          )}
          <AppSaveAction
            source={source}
            ready={favoriteReady}
            saving={favoriteSaving}
            appSaved={appSaved}
            onToggle={handleFavoriteToggle}
            compact
          />
          <Button asChild>
            <Link
              to={hasProgress ? continuePathForHistory : readPathForDefault}
              state={location.state}
            >
              {hasProgress ? 'Continue' : 'Read'}
            </Link>
          </Button>
        </div>
      </div>
      <DebugReportFallbackDialog
        reportText={manualDebugReportText}
        onOpenChange={(open) => {
          if (!open) setManualDebugReportText(null);
        }}
      />
    </BlacklistWarning>
  );
}

function ReadGroups({
  sourceId,
  comicId,
  groups,
  currentHistory,
  locationState,
}: {
  sourceId: string;
  comicId: string;
  groups: ReadGroup[];
  currentHistory: HistoryRow | null;
  locationState: unknown;
}) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const [reverseOrder, setReverseOrder] = useState(false);
  const displayedGroups = reverseOrder
    ? groups.map((group) => ({
        ...group,
        units: [...group.units].reverse(),
      }))
    : groups;
  const canReverseOrder = groups.some((group) => group.units.length > 1);

  function toggleGroup(groupId: string) {
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Chapters</h2>
        {canReverseOrder && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={reverseOrder}
            onClick={() => setReverseOrder((current) => !current)}
          >
            {reverseOrder ? 'Oldest first' : 'Newest first'}
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-3">
        {displayedGroups.map((group) => {
          const open = openGroups.has(group.id);
          return (
            <div key={group.id} className="rounded-lg border border-border/60 p-3">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left"
                aria-expanded={open}
                onClick={() => toggleGroup(group.id)}
              >
                <span>
                  <span className="block text-sm font-medium">{group.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {group.units.length} {group.units.length === 1 ? 'chapter' : 'chapters'}
                  </span>
                </span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
              </button>
              {open && (
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {group.units.map((unit) => (
                    <ReadUnitRow
                      key={unit.id}
                      sourceId={sourceId}
                      comicId={comicId}
                      group={group}
                      unit={unit}
                      history={currentHistory?.readUnitId === unit.id ? currentHistory : null}
                      locationState={locationState}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ReadUnitRow({
  sourceId,
  comicId,
  group,
  unit,
  history,
  locationState,
}: {
  sourceId: string;
  comicId: string;
  group: ReadGroup;
  unit: ReadUnit;
  history: HistoryRow | null;
  locationState: unknown;
}) {
  const page = history?.lastPage ?? 1;
  return (
    <Link
      to={readPathForUnit(sourceId, comicId, unit.id, page)}
      state={locationState}
      className="rounded-md border border-border/40 p-2 text-sm hover:bg-accent/30"
    >
      <span className="block font-medium">{unit.title}</span>
      <span className="block text-xs text-muted-foreground">
        {history ? `Continue page ${history.lastPage}/${history.totalPages}` : group.title}
      </span>
    </Link>
  );
}

function readPathForUnit(sourceId: string, comicId: string, unitId: string, page: number): string {
  const params = new URLSearchParams({ unit: unitId });
  return `/source/${sourceId}/comic/${encodeURIComponent(comicId)}/read/${page}?${params.toString()}`;
}
